import { z } from 'zod'

import { CUSTOMER_STATUSES, KYC_STATUSES } from '@/features/customers/constants'

/**
 * The customer screens keep their state in the URL.
 *
 * A filtered list is a place, not a mood: a supervisor asked "which files are still pending at
 * Kaneshie?" should be able to send that answer as a link, and a teller who opens a customer and
 * presses Back should land on the same page of the same search they left. Every value is read
 * back through `.catch()`, so a hand-edited or stale URL degrades to the default rather than
 * throwing an operator out of the screen.
 *
 * Every member is optional. A parameter that is simply absent is the default, which keeps a plain
 * `/customers` link valid from anywhere in the application and keeps the address bar free of
 * values nobody chose.
 */

export const DEFAULT_PAGE_SIZE = 25

export const customerListSearchSchema = z.object({
  search: z.string().trim().min(1).optional().catch(undefined),
  status: z.enum(CUSTOMER_STATUSES).optional().catch(undefined),
  kycStatus: z.enum(KYC_STATUSES).optional().catch(undefined),
  branchId: z.string().min(1).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
  pageSize: z.number().int().min(10).max(200).optional().catch(undefined),
})

export type CustomerListSearch = z.infer<typeof customerListSearchSchema>

export const CUSTOMER_DETAIL_TABS = [
  'overview',
  'addresses',
  'identity',
  'nextOfKin',
  'consents',
  'media',
  'audit',
] as const

export type CustomerDetailTab = (typeof CUSTOMER_DETAIL_TABS)[number]

export const customerDetailSearchSchema = z.object({
  tab: z.enum(CUSTOMER_DETAIL_TABS).optional().catch(undefined),
})
